import React from 'react';
import {
    BooleanField,
    BooleanInput,
    Create,
    Datagrid,
    Edit,
    EditButton,
    List,
    maxLength,
    NumberInput,
    SelectInput,
    SimpleForm,
    TextField,
    NumberField,
    TextInput,
    required,
    minValue,
    number,
    choices,
    SelectField,
} from 'react-admin';
import * as FaSolidIcons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Grid,
    Typography,
    Box,
} from "@mui/material";

// Validation configuration
const validateText = [maxLength(30, "Bitte maximal 30 Zeichen eingeben"), required("Dieses Feld ist ein Pflichtfeld")]
const validateOrder = [choices([1, 2, 3]), required("Dieses Feld ist ein Pflichtfeld")]
const validateValue = [minValue(0), number(1000000000), required("Dieses Feld ist ein Pflichtfeld")]

// Choices for background and text colors
const backgroundColorChoices = [
    { id: 'bg-light', name: 'Hell (Standard)' },
    { id: 'bg-primary', name: 'Primär' },
    { id: 'bg-secondary', name: 'Sekundär' },
];

const textColorChoices = [
    { id: 'text-primary', name: 'Primär (Standard)' },
    { id: 'text-secondary', name: 'Sekundär' },
    { id: 'text-dark', name: 'Dunkel' },
    { id: 'text-light', name: 'Hell' },
];

export const MetricsList = (props) => (
    <List {...props}>
        <Datagrid>
            <NumberField label="Reihenfolge" source="order" />
            <TextField label="Kennzeichnung" source="label" />
            <NumberField label="Wert" source="value" />
            <BooleanField label="Ist Sichtbar" source="isVisible" />
            <TextField label="Einheit" source="unit" />
            <SelectField label="Icon" source="icon" choices={iconChoices} />
            <SelectField label="Hintergrund" source="backgroundClass" choices={backgroundColorChoices} />
            <SelectField label="Icon-Farbe" source="iconColor" choices={textColorChoices} />
            <SelectField label="Wertfarbe" source="valueColor" choices={textColorChoices} />
            <SelectField label="Labelfarbe" source="labelColor" choices={textColorChoices} />
            <EditButton />
        </Datagrid>
    </List>
);

const iconChoices = [
    { id: 'faGraduationCap', name: 'Absolventenkappe' },
    { id: 'faChartBar', name: 'Balkendiagramm' },
    { id: 'faLeaf', name: 'Blatt' },
    { id: 'faMouse', name: 'Computermaus' },
    { id: 'faFile', name: 'Datei' },
    { id: 'faEuroSign', name: 'Euro-Symbol' },
    { id: 'faFutbol', name: 'Fußball' },
    { id: 'faCheckCircle', name: 'Haken auf Kreis' },
    { id: 'faHandHoldingUsd', name: 'Hand mit USD-Symbol' },
    { id: 'faHeart', name: 'Herz' },
    { id: 'faBirthdayCake', name: 'Jubiläum' },
    { id: 'faCalendar', name: 'Kalender' },
    { id: 'faCalendarAlt', name: 'Kalender (Alternative)' },
    { id: 'faMask', name: 'Karnevalsgruppen' },
    { id: 'faVolumeUp', name: 'Lauter' },
    { id: 'faCoins', name: 'Münzenstapel' },
    { id: 'faBoxOpen', name: 'Offene Kiste' },
    { id: 'faAward', name: 'Orden' },
    { id: 'faFileAlt', name: 'Papierblatt' },
    { id: 'faUser', name: 'Person' },
    { id: 'faUserFriends', name: 'Personen (Zwei)' },
    { id: 'faUsers', name: 'Personen (Drei)' },
    { id: 'faTrophy', name: 'Pokal' },
    { id: 'faProjectDiagram', name: 'Projektdiagramm' },
    { id: 'faLightbulb', name: 'Glühlampe' },
    { id: 'faRocket', name: 'Rakete' },
    { id: 'faPiggyBank', name: 'Sparschwein' },
    { id: 'faVoteYea', name: 'Stimmzettel' },
];

// Helper to dynamically get the icon object
const getFaIcon = (iconId) => {
    return FaSolidIcons[iconId] || null;
};

const IconOptionRenderer = (choice) => {
    const IconComponent = getFaIcon(choice.id);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            // Important: This Box styles the MenuItem content
            padding: '4px 0',
            '& .fa-icon': {
                width: '1em',
                textAlign: 'center',
                color: 'primary.main'
            }
        }}>
            {IconComponent ? (
                // Icon
                <Box component="span" className="fa-icon">
                    <FontAwesomeIcon icon={IconComponent} />
                </Box>
            ) : (
                // Fallback for missing icon
                <Box component="span" className="fa-icon" sx={{ color: 'error.main' }}>
                    ?
                </Box>
            )}
            {/* Name */}
            <Typography component="span">{choice.name}</Typography>
        </Box>
    );
};

const MetricsForm = (props) => (
    <SimpleForm {...props}>
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <Grid item xs={12}>
                    <Typography variant="h4">
                        {props.title}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <BooleanInput label="Engagement-KPI ist sichtbar" source="isVisible" />
                </Grid>
                <Grid item xs={12}>
                    <SelectInput
                        style={{ marginTop: 0 }}
                        fullWidth
                        label="Position in Projektliste"
                        source="order"
                        choices={[
                            { id: 1, name: '1' },
                            { id: 2, name: '2' },
                            { id: 3, name: '3' }
                        ]} validate={validateOrder}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SelectInput
                        style={{ marginTop: 0 }}
                        fullWidth
                        label="Hintergrundklasse"
                        source="backgroundClass"
                        choices={backgroundColorChoices}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SelectInput
                        style={{ marginTop: 0 }}
                        fullWidth
                        label="Icon-Farbe"
                        source="iconColor"
                        choices={textColorChoices}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SelectInput
                        style={{ marginTop: 0 }}
                        fullWidth
                        label="Wertfarbe"
                        source="valueColor"
                        choices={textColorChoices}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SelectInput
                        style={{ marginTop: 0 }}
                        fullWidth
                        label="Labelfarbe"
                        source="labelColor"
                        choices={textColorChoices}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SelectInput
                        style={{ marginTop: 0 }}
                        fullWidth
                        label="Icon"
                        source="icon"
                        choices={iconChoices}
                        optionText={IconOptionRenderer}
                    />
                </Grid>
                <Grid item xs={12}>
                    <NumberInput fullWidth label="Wert" source="value" validate={validateValue} />
                </Grid>
                <Grid item xs={12}>
                    <TextInput fullWidth label="Einheit" source="unit" />
                </Grid>
                <Grid item xs={12}>
                    <TextInput fullWidth label="Untertitel" source="label" validate={validateText} />
                </Grid>
            </Grid>
        </Grid>
    </SimpleForm>
);

export const MetricsCreate = (props) => (
    <Create {...props} redirect="list">
        <MetricsForm {...props} title="Neues Engagement-KPI anlegen" />
    </Create>
);

export const MetricsEdit = (props) => (
    <Edit {...props} redirect="list">
        <MetricsForm {...props} title="Engagement-KPI bearbeiten" />
    </Edit>
);